import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faBold,
  faDownload,
  faEraser,
  faEye,
  faFileAlt,
  faGripLines,
  faHeading,
  faImage,
  faItalic,
  faListOl,
  faListUl,
  faPen,
  faPlus,
  faRedo,
  faSave,
  faTable,
  faTrashAlt,
  faUnderline,
  faUndo,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import PromotoraService, { PromotoraContratoTemplateDTO, VariavelContratoPromotora } from '../../../services/promotoraService';
import Spinner from '../../../shared/spinner';

interface Props {
  show: boolean;
  onHide: () => void;
}

const conteudoPadrao = `
  <h2 style="text-align: center;">Contrato de Gestão Promotora</h2>
  <p><strong>Promotora:</strong> {{nome promotora}}</p>
  <p><strong>CNPJ:</strong> {{cnpj promotora}}</p>
  <p><strong>Sócio:</strong> {{nome socio 1}}</p>
  <p><strong>CPF/CNPJ:</strong> {{cpf socio 1}}</p>
  <p><strong>E-mail:</strong> {{email socio 1}}</p>
  <p><br /></p>
  <p>São Paulo, {{data atual}}</p>
  <p><br /></p>
  <p>________________________________________</p>
  <p>{{nome socio 1}}</p>
`;

const TIPO_TEMPLATE_HTML = 'HTML';
const TIPO_TEMPLATE_ARQUIVO = 'ARQUIVO';
const EXTENSOES_TEMPLATE_ARQUIVO = ['pdf', 'doc', 'docx'];

const montarHtmlContrato = (conteudo: string) => `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <style>
      body {
        color: #222;
        font-family: Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        margin: 32px;
      }
      p {
        margin: 0 0 10px;
      }
      img {
        height: auto;
        max-width: 100%;
      }
      table {
        border-collapse: collapse;
        margin: 8px 0 12px;
        max-width: 100%;
        table-layout: auto;
      }
      table:not([style]) {
        width: 100%;
      }
      td, th {
        padding: 6px;
        vertical-align: top;
      }
      td:not([style]), th:not([style]) {
        border: 1px solid #555;
      }
      .contrato-header {
        border-bottom: 1px solid #ddd;
        margin-bottom: 20px;
        padding-bottom: 10px;
      }
      .contrato-footer {
        border-top: 1px solid #ddd;
        color: #666;
        font-size: 11px;
        margin-top: 24px;
        padding-top: 10px;
      }
      .contrato-page-break {
        break-after: page;
        page-break-after: always;
      }
      @media print {
        @page {
          margin: 18mm 16mm;
          size: A4;
        }
      }
    </style>
  </head>
  <body>
    ${conteudo || '<p><br /></p>'}
  </body>
</html>`;

const templateVazio = (): PromotoraContratoTemplateDTO => ({
  ativo: true,
  nome: '',
  tipoTemplate: TIPO_TEMPLATE_HTML,
  corpoHtml: montarHtmlContrato(conteudoPadrao),
});

function PromotoraContratoTemplateModal({ show, onHide }: Props) {
  const promotoraService = new PromotoraService();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputImagemRef = useRef<HTMLInputElement | null>(null);
  const inputArquivoTemplateRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'warning' | 'danger'; text: string } | null>(null);
  const [templates, setTemplates] = useState<PromotoraContratoTemplateDTO[]>([]);
  const [variaveis, setVariaveis] = useState<VariavelContratoPromotora[]>([]);
  const [template, setTemplate] = useState<PromotoraContratoTemplateDTO>(templateVazio());
  const [editorHtml, setEditorHtml] = useState(conteudoPadrao);
  const [editorVersion, setEditorVersion] = useState(0);
  const [previewHtml, setPreviewHtml] = useState('');

  const extrairConteudoContrato = (html?: string) => {
    const valor = (html || '').trim();
    if (!valor) {
      return conteudoPadrao;
    }

    try {
      const documento = new DOMParser().parseFromString(valor, 'text/html');
      const corpo = documento.body?.innerHTML?.trim();
      return corpo || valor;
    } catch (err) {
      return valor;
    }
  };

  const carregarConteudoNoEditor = (html?: string) => {
    const conteudo = extrairConteudoContrato(html);
    setEditorHtml(conteudo);
    setEditorVersion((versao) => versao + 1);
    return conteudo;
  };

  const corpoHtmlAtual = () => {
    const conteudo = editorRef.current?.innerHTML || editorHtml || conteudoPadrao;
    return montarHtmlContrato(conteudo);
  };

  const editorTemConteudo = () => {
    const texto = editorRef.current?.innerText?.replace(/\u00a0/g, ' ').trim() || '';
    const html = editorRef.current?.innerHTML || '';
    return Boolean(texto || /<(img|table|hr)\b/i.test(html));
  };

  const atualizarConteudoEditor = () => {
    const html = corpoHtmlAtual();
    setTemplate((atual) => ({ ...atual, corpoHtml: html }));
  };

  const moverCursorParaFim = () => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const selecaoDentroEditor = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      return false;
    }

    const node = selection.anchorNode;
    return Boolean(node && editor.contains(node));
  };

  const aplicarComando = (comando: string, valor?: string) => {
    editorRef.current?.focus();
    document.execCommand(comando, false, valor);
    atualizarConteudoEditor();
  };

  const inserirHtmlNoEditor = (html: string) => {
    if (!html) return;
    if (!selecaoDentroEditor()) {
      moverCursorParaFim();
    }

    document.execCommand('insertHTML', false, html);
    atualizarConteudoEditor();
  };

  const escaparHtml = (texto: string) => texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const isTemplateArquivo = (item: PromotoraContratoTemplateDTO = template) => (
    String(item.tipoTemplate || '').toUpperCase() === TIPO_TEMPLATE_ARQUIVO
    || Boolean(item.arquivoBase64 || item.nomeArquivo)
  );

  const limparPrefixoBase64 = (valor: string) => {
    const base64 = String(valor || '');
    return base64.includes(',') ? base64.split(',').pop() || '' : base64;
  };

  const extensaoArquivoAtual = () => String(template.extensaoArquivo || '').toLowerCase();

  const obterMimeArquivoTemplate = (extensaoArquivo?: string) => {
    const extensao = String(extensaoArquivo || '').toLowerCase();
    if (extensao === 'pdf') {
      return 'application/pdf';
    }
    if (extensao === 'doc') {
      return 'application/msword';
    }
    if (extensao === 'docx') {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    return 'application/octet-stream';
  };

  const base64ParaBlob = (valorBase64?: string, contentType = 'application/octet-stream') => {
    const base64 = limparPrefixoBase64(valorBase64 || '');
    const byteCharacters = window.atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = Array.from(slice).map((char) => char.charCodeAt(0));
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  };

  const nomeDownloadTemplate = () => {
    const nomeArquivo = template.nomeArquivo || template.nome || 'template-contrato';
    if (nomeArquivo.includes('.')) {
      return nomeArquivo;
    }
    return `${nomeArquivo}.${template.extensaoArquivo || 'docx'}`;
  };

  const baixarArquivoTemplate = () => {
    if (!template.arquivoBase64) {
      setAlert({ type: 'warning', text: 'Importe o arquivo do template.' });
      return;
    }

    const blob = base64ParaBlob(template.arquivoBase64, obterMimeArquivoTemplate(template.extensaoArquivo));
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeDownloadTemplate();
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const abrirArquivoTemplate = () => {
    if (!template.arquivoBase64) {
      setAlert({ type: 'warning', text: 'Importe o arquivo do template.' });
      return;
    }

    const blob = base64ParaBlob(template.arquivoBase64, obterMimeArquivoTemplate(template.extensaoArquivo));
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const extrairExtensaoArquivo = (nomeArquivo: string) => {
    const partes = String(nomeArquivo || '').split('.');
    return partes.length > 1 ? String(partes.pop() || '').toLowerCase() : '';
  };

  const removerExtensaoArquivo = (nomeArquivo: string) => {
    const nome = String(nomeArquivo || '').trim();
    const indice = nome.lastIndexOf('.');
    return indice > 0 ? nome.substring(0, indice) : nome;
  };

  const montarHtmlArquivoImportado = (nomeArquivo?: string, extensaoArquivo?: string) => montarHtmlContrato(`
    <p><strong>Arquivo importado:</strong> ${escaparHtml(nomeArquivo || '')}</p>
    <p><strong>Tipo:</strong> ${escaparHtml(String(extensaoArquivo || '').toUpperCase())}</p>
  `);

  const sanitizarHtmlColado = (html: string) => {
    try {
      const documento = new DOMParser().parseFromString(html, 'text/html');
      documento.querySelectorAll('script, object, embed, iframe, meta, link').forEach((elemento) => elemento.remove());
      documento.querySelectorAll('*').forEach((elemento) => {
        Array.from(elemento.attributes).forEach((atributo) => {
          const nome = atributo.name.toLowerCase();
          const valor = atributo.value.trim().toLowerCase();
          if (nome.startsWith('on') || ((nome === 'href' || nome === 'src') && valor.startsWith('javascript:'))) {
            elemento.removeAttribute(atributo.name);
          }
        });
      });

      const estilos = Array.from(documento.head.querySelectorAll('style'))
        .map((style) => style.outerHTML)
        .join('');
      return `${estilos}${documento.body?.innerHTML || html}`;
    } catch {
      return html;
    }
  };

  const lerArquivoComoDataUrl = (arquivo: File) => new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsDataURL(arquivo);
  });

  const lerImagensColadas = async (clipboardData: any) => {
    const itens = Array.from(clipboardData?.items || []) as DataTransferItem[];
    const arquivos = itens
      .filter((item) => item.kind === 'file' && String(item.type || '').startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((arquivo): arquivo is File => Boolean(arquivo));

    const imagens = await Promise.all(arquivos.map(lerArquivoComoDataUrl));
    return imagens.filter(Boolean);
  };

  const aplicarImagensColadas = (html: string, imagens: string[]) => {
    if (imagens.length === 0) {
      return html;
    }

    try {
      const documento = new DOMParser().parseFromString(html, 'text/html');
      const tagsImagem = Array.from(documento.body.querySelectorAll('img'));
      let indiceImagem = 0;

      tagsImagem.forEach((imagem) => {
        const src = imagem.getAttribute('src') || '';
        const precisaSubstituir = !src || src.startsWith('file:') || src.startsWith('cid:')
          || src.startsWith('blob:') || !src.startsWith('data:');
        if (!precisaSubstituir || indiceImagem >= imagens.length) {
          return;
        }

        const estiloAtual = imagem.getAttribute('style') || '';
        imagem.setAttribute('src', imagens[indiceImagem]);
        if (!/max-width/i.test(estiloAtual)) {
          imagem.setAttribute('style', `${estiloAtual}; height: auto; max-width: 100%;`);
        }
        indiceImagem += 1;
      });

      const imagensRestantes = imagens.slice(indiceImagem)
        .map((src) => `<p style="text-align: center;"><img src="${src}" alt="Imagem do contrato" style="height: auto; max-width: 100%;" /></p>`)
        .join('');
      const estilos = Array.from(documento.head.querySelectorAll('style'))
        .map((style) => style.outerHTML)
        .join('');
      return `${estilos}${documento.body?.innerHTML || html}${imagensRestantes}`;
    } catch {
      return `${html}${imagens.map((src) => `<p><img src="${src}" alt="Imagem do contrato" style="height: auto; max-width: 100%;" /></p>`).join('')}`;
    }
  };

  const colarConteudoFormatado = async (event: any) => {
    const clipboardData = event.clipboardData;
    const html = clipboardData?.getData('text/html');
    const texto = clipboardData?.getData('text/plain');
    if (!html && !texto) {
      return;
    }

    const selection = window.getSelection();
    const rangeColagem = selecaoDentroEditor() && selection?.rangeCount
      ? selection.getRangeAt(0).cloneRange()
      : null;
    event.preventDefault();
    const imagens = await lerImagensColadas(clipboardData);
    const selectionAtual = window.getSelection();
    if (rangeColagem && selectionAtual) {
      selectionAtual.removeAllRanges();
      selectionAtual.addRange(rangeColagem);
    }

    if (html) {
      inserirHtmlNoEditor(aplicarImagensColadas(sanitizarHtmlColado(html), imagens));
      return;
    }

    const imagensHtml = imagens
      .map((src) => `<p style="text-align: center;"><img src="${src}" alt="Imagem do contrato" style="height: auto; max-width: 100%;" /></p>`)
      .join('');
    inserirHtmlNoEditor(`<p>${escaparHtml(texto).replace(/\r\n|\n|\r/g, '<br />')}</p>${imagensHtml}`);
  };

  const inserirCabecalho = () => {
    inserirHtmlNoEditor(`
      <div class="contrato-header" style="border-bottom: 1px solid #ddd; margin-bottom: 20px; padding-bottom: 10px;">
        <p style="text-align: center;"><strong>Cabeçalho do contrato</strong></p>
      </div>
      <p><br /></p>
    `);
  };

  const inserirRodape = () => {
    inserirHtmlNoEditor(`
      <div class="contrato-footer" style="border-top: 1px solid #ddd; color: #666; font-size: 11px; margin-top: 24px; padding-top: 10px;">
        <p style="text-align: center;">Rodapé do contrato</p>
      </div>
      <p><br /></p>
    `);
  };

  const inserirQuebraPagina = () => {
    inserirHtmlNoEditor('<div class="contrato-page-break" style="page-break-after: always; break-after: page;">&nbsp;</div><p><br /></p>');
  };

  const montarTabelaHtml = (linhas: number, colunas: number) => {
    const linhasHtml = Array.from({ length: linhas }, () => {
      const colunasHtml = Array.from({ length: colunas }, () => (
        '<td style="border: 1px solid #555; padding: 6px; vertical-align: top;">&nbsp;</td>'
      )).join('');
      return `<tr>${colunasHtml}</tr>`;
    }).join('');

    return `<table style="border-collapse: collapse; margin: 8px 0 12px; table-layout: auto; width: 100%;"><tbody>${linhasHtml}</tbody></table><p><br /></p>`;
  };

  const inserirTabela = () => {
    const linhas = Number(window.prompt('Linhas', '3'));
    const colunas = Number(window.prompt('Colunas', '3'));
    if (!Number.isInteger(linhas) || !Number.isInteger(colunas) || linhas < 1 || colunas < 1) {
      return;
    }

    inserirHtmlNoEditor(montarTabelaHtml(Math.min(linhas, 20), Math.min(colunas, 10)));
  };

  const selecionarLogomarca = () => {
    inputImagemRef.current?.click();
  };

  const carregarLogomarca = (event: any) => {
    const arquivo = event.target.files?.[0];
    if (!arquivo) return;

    if (!String(arquivo.type || '').startsWith('image/')) {
      setAlert({ type: 'warning', text: 'Selecione uma imagem para inserir no contrato.' });
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result || '');
      inserirHtmlNoEditor(`
        <p style="text-align: center;">
          <img src="${src}" alt="Logomarca" style="height: auto; max-width: 180px;" />
        </p>
      `);
      event.target.value = '';
    };
    reader.readAsDataURL(arquivo);
  };

  const importarArquivoTemplate = async (event: any) => {
    const arquivo = event.target.files?.[0];
    if (!arquivo) return;

    const extensao = extrairExtensaoArquivo(arquivo.name);
    if (!EXTENSOES_TEMPLATE_ARQUIVO.includes(extensao)) {
      setAlert({ type: 'warning', text: 'Importe um arquivo PDF, DOC ou DOCX.' });
      event.target.value = '';
      return;
    }

    const dataUrl = await lerArquivoComoDataUrl(arquivo);
    const arquivoBase64 = limparPrefixoBase64(dataUrl);
    const corpoHtml = montarHtmlArquivoImportado(arquivo.name, extensao);
    setTemplate((atual) => ({
      ...atual,
      nome: atual.nome?.trim() ? atual.nome : removerExtensaoArquivo(arquivo.name),
      tipoTemplate: TIPO_TEMPLATE_ARQUIVO,
      corpoHtml,
      nomeArquivo: arquivo.name,
      extensaoArquivo: extensao,
      arquivoBase64,
    }));
    setPreviewHtml('');
    carregarConteudoNoEditor(corpoHtml);
    event.target.value = '';
  };

  const alterarTipoTemplate = (tipoTemplate: string) => {
    if (tipoTemplate === TIPO_TEMPLATE_ARQUIVO) {
      setTemplate((atual) => ({
        ...atual,
        tipoTemplate: TIPO_TEMPLATE_ARQUIVO,
        corpoHtml: atual.corpoHtml || montarHtmlArquivoImportado(atual.nomeArquivo, atual.extensaoArquivo),
      }));
      setPreviewHtml('');
      return;
    }

    const conteudo = carregarConteudoNoEditor(template.corpoHtml);
    setTemplate((atual) => ({
      ...atual,
      tipoTemplate: TIPO_TEMPLATE_HTML,
      corpoHtml: montarHtmlContrato(conteudo),
      nomeArquivo: undefined,
      extensaoArquivo: undefined,
      arquivoBase64: undefined,
    }));
    setPreviewHtml('');
  };

  const carregarDados = async () => {
    setLoading(true);
    setAlert(null);
    try {
      const [templatesResp, variaveisResp] = await Promise.all([
        promotoraService.listarTemplatesContratoPromotora(),
        promotoraService.listarVariaveisTemplateContratoPromotora()
      ]);
      setTemplates(templatesResp.data || []);
      setVariaveis(variaveisResp.data || []);
    } catch (err: any) {
      setAlert({ type: 'danger', text: err.response?.data || 'Erro ao carregar templates de contrato.' });
    } finally {
      setLoading(false);
    }
  };

  const atualizarTemplate = (campo: keyof PromotoraContratoTemplateDTO, valor: any) => {
    setTemplate((atual) => ({ ...atual, [campo]: valor }));
  };

  const novoTemplate = () => {
    const novo = templateVazio();
    const conteudo = carregarConteudoNoEditor(novo.corpoHtml);
    setTemplate({ ...novo, corpoHtml: montarHtmlContrato(conteudo) });
    setPreviewHtml('');
  };

  const editarTemplate = async (item: PromotoraContratoTemplateDTO) => {
    setLoading(true);
    try {
      const response = item.id
        ? await promotoraService.buscarTemplateContratoPromotora(item.id)
        : { data: item };
      const detalhe = response.data || item;
      const templateArquivo = isTemplateArquivo(detalhe);
      const corpoHtml = templateArquivo
        ? detalhe.corpoHtml || montarHtmlArquivoImportado(detalhe.nomeArquivo, detalhe.extensaoArquivo)
        : montarHtmlContrato(carregarConteudoNoEditor(detalhe.corpoHtml));

      if (templateArquivo) {
        carregarConteudoNoEditor(corpoHtml);
      }

      setTemplate({
        ...templateVazio(),
        ...detalhe,
        tipoTemplate: templateArquivo ? TIPO_TEMPLATE_ARQUIVO : TIPO_TEMPLATE_HTML,
        ativo: detalhe.ativo !== false,
        corpoHtml,
      });
      setPreviewHtml('');
    } catch (err: any) {
      setAlert({ type: 'danger', text: err.response?.data || 'Erro ao carregar template de contrato.' });
    } finally {
      setLoading(false);
    }
  };

  const inserirVariavel = (variavel: string) => {
    if (!selecaoDentroEditor()) {
      moverCursorParaFim();
    }

    document.execCommand('insertText', false, variavel);
    atualizarConteudoEditor();
  };

  const salvarTemplate = async () => {
    const templateArquivo = isTemplateArquivo();
    if (!template.nome?.trim()) {
      setAlert({ type: 'warning', text: 'Informe o nome do template.' });
      return;
    }
    if (!templateArquivo && !editorTemConteudo()) {
      setAlert({ type: 'warning', text: 'Informe o conteúdo do template.' });
      return;
    }
    if (templateArquivo && !template.arquivoBase64) {
      setAlert({ type: 'warning', text: 'Importe o arquivo do template.' });
      return;
    }

    setLoading(true);
    try {
      await promotoraService.salvarTemplateContratoPromotora({
        ...template,
        nome: template.nome.trim(),
        tipoTemplate: templateArquivo ? TIPO_TEMPLATE_ARQUIVO : TIPO_TEMPLATE_HTML,
        corpoHtml: templateArquivo
          ? template.corpoHtml || montarHtmlArquivoImportado(template.nomeArquivo, template.extensaoArquivo)
          : corpoHtmlAtual(),
      });
      setAlert({ type: 'success', text: 'Template salvo com sucesso.' });
      novoTemplate();
      await carregarDados();
    } catch (err: any) {
      setAlert({ type: 'danger', text: err.response?.data || 'Erro ao salvar template.' });
    } finally {
      setLoading(false);
    }
  };

  const excluirTemplate = async (item: PromotoraContratoTemplateDTO) => {
    if (!item.id || !window.confirm('Inativar este template de contrato?')) {
      return;
    }

    setLoading(true);
    try {
      await promotoraService.excluirTemplateContratoPromotora(item.id);
      setAlert({ type: 'success', text: 'Template inativado com sucesso.' });
      await carregarDados();
    } catch (err: any) {
      setAlert({ type: 'danger', text: err.response?.data || 'Erro ao inativar template.' });
    } finally {
      setLoading(false);
    }
  };

  const preVisualizar = async () => {
    const templateArquivo = isTemplateArquivo();
    if (!templateArquivo && !editorTemConteudo()) {
      setAlert({ type: 'warning', text: 'Informe o conteúdo para pré-visualizar.' });
      return;
    }
    if (templateArquivo && !template.arquivoBase64) {
      setAlert({ type: 'warning', text: 'Importe o arquivo para pré-visualizar.' });
      return;
    }

    setLoading(true);
    try {
      const templatePreview = {
        ...template,
        tipoTemplate: templateArquivo ? TIPO_TEMPLATE_ARQUIVO : TIPO_TEMPLATE_HTML,
        corpoHtml: templateArquivo
          ? template.corpoHtml || montarHtmlArquivoImportado(template.nomeArquivo, template.extensaoArquivo)
          : corpoHtmlAtual(),
      };
      const response = await promotoraService.preVisualizarTemplateContratoPromotora(templatePreview);
      setPreviewHtml(response.data?.htmlPreVisualizacao || templatePreview.corpoHtml || '');
    } catch (err: any) {
      setAlert({ type: 'danger', text: err.response?.data || 'Erro ao gerar pré-visualização.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      carregarDados();
      novoTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = editorHtml;
    }
  }, [editorVersion, editorHtml]);

  return (
    <Modal show={show} onHide={onHide} fullscreen>
      <Modal.Header closeButton>
        <Modal.Title>Templates de contrato</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner loading={loading} />}
        {alert && <Alert variant={alert.type}>{alert.text}</Alert>}

        <Row className="g-3">
          <Col lg={4}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Templates cadastrados</h6>
              <Button size="sm" variant="outline-primary" onClick={novoTemplate}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Novo
              </Button>
            </div>
            <div className="table-responsive border rounded">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((item) => (
                    <tr key={item.id}>
                      <td>{item.nome}</td>
                      <td>{isTemplateArquivo(item) ? 'Word/PDF' : 'HTML'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => editarTemplate(item)} title="Editar">
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => excluirTemplate(item)} title="Inativar">
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {templates.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-muted text-center">Nenhum template cadastrado.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <h6 className="mt-3">Variáveis</h6>
            <div className="border rounded p-2" style={{ maxHeight: 250, overflowY: 'auto' }}>
              {variaveis.map((item) => (
                <Button
                  key={item.variavel}
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  className="me-1 mb-1"
                  title={item.descricao}
                  onClick={() => inserirVariavel(item.variavel)}
                >
                  {item.variavel}
                </Button>
              ))}
            </div>
          </Col>

          <Col lg={8}>
            <Row className="g-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nome do template</Form.Label>
                  <Form.Control value={template.nome || ''} onChange={(e) => atualizarTemplate('nome', e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select
                    value={isTemplateArquivo() ? TIPO_TEMPLATE_ARQUIVO : TIPO_TEMPLATE_HTML}
                    onChange={(e) => alterarTipoTemplate(e.target.value)}
                  >
                    <option value={TIPO_TEMPLATE_HTML}>Editor HTML</option>
                    <option value={TIPO_TEMPLATE_ARQUIVO}>Word/PDF</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Check
                  type="switch"
                  label="Ativo"
                  checked={template.ativo !== false}
                  onChange={(e) => atualizarTemplate('ativo', e.target.checked)}
                />
              </Col>
              <Col md={12}>
                <Form.Label>{isTemplateArquivo() ? 'Arquivo do template' : 'Editor do contrato'}</Form.Label>
                {isTemplateArquivo() ? (
                  <div className="border rounded p-3">
                    <input
                      ref={inputArquivoTemplateRef}
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={importarArquivoTemplate}
                      style={{ display: 'none' }}
                    />
                    {template.nomeArquivo && (
                      <div className="d-flex align-items-center gap-2 mt-3 text-muted">
                        <FontAwesomeIcon icon={faFileAlt} />
                        <span>{template.nomeArquivo}</span>
                        <span>{String(template.extensaoArquivo || '').toUpperCase()}</span>
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <Button type="button" size="sm" variant="outline-primary" onClick={() => inputArquivoTemplateRef.current?.click()}>
                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                        {template.arquivoBase64 ? 'Substituir Word/PDF' : 'Importar Word/PDF'}
                      </Button>
                      {template.arquivoBase64 && (
                        <Button type="button" size="sm" variant="outline-secondary" onClick={baixarArquivoTemplate}>
                          <FontAwesomeIcon icon={faDownload} className="me-2" />
                          Baixar para editar
                        </Button>
                      )}
                      {template.arquivoBase64 && extensaoArquivoAtual() === 'pdf' && (
                        <Button type="button" size="sm" variant="outline-secondary" onClick={abrirArquivoTemplate}>
                          <FontAwesomeIcon icon={faEye} className="me-2" />
                          Abrir PDF
                        </Button>
                      )}
                    </div>
                    {template.arquivoBase64 && extensaoArquivoAtual() === 'pdf' && (
                      <div className="mt-3 border rounded overflow-hidden" style={{ height: 420 }}>
                        <embed
                          src={`data:application/pdf;base64,${limparPrefixoBase64(template.arquivoBase64)}`}
                          type="application/pdf"
                          style={{ border: 0, height: '100%', width: '100%' }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border rounded">
                    <input
                      ref={inputImagemRef}
                      type="file"
                      accept="image/*"
                      onChange={carregarLogomarca}
                      style={{ display: 'none' }}
                    />
                    <div className="d-flex flex-wrap align-items-center gap-1 border-bottom bg-light p-2">
                      <Form.Select
                        size="sm"
                        style={{ width: 150 }}
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) {
                            aplicarComando('formatBlock', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">Estilo</option>
                        <option value="<p>">Parágrafo</option>
                        <option value="<h1>">Título 1</option>
                        <option value="<h2>">Título 2</option>
                        <option value="<h3>">Título 3</option>
                      </Form.Select>

                      <Button size="sm" variant="outline-secondary" title="Desfazer" onClick={() => aplicarComando('undo')}>
                        <FontAwesomeIcon icon={faUndo} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Refazer" onClick={() => aplicarComando('redo')}>
                        <FontAwesomeIcon icon={faRedo} />
                      </Button>
                      <span className="mx-1 text-muted">|</span>
                      <Button size="sm" variant="outline-secondary" title="Negrito" onClick={() => aplicarComando('bold')}>
                        <FontAwesomeIcon icon={faBold} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Itálico" onClick={() => aplicarComando('italic')}>
                        <FontAwesomeIcon icon={faItalic} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Sublinhado" onClick={() => aplicarComando('underline')}>
                        <FontAwesomeIcon icon={faUnderline} />
                      </Button>
                      <span className="mx-1 text-muted">|</span>
                      <Button size="sm" variant="outline-secondary" title="Lista com marcadores" onClick={() => aplicarComando('insertUnorderedList')}>
                        <FontAwesomeIcon icon={faListUl} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Lista numerada" onClick={() => aplicarComando('insertOrderedList')}>
                        <FontAwesomeIcon icon={faListOl} />
                      </Button>
                      <span className="mx-1 text-muted">|</span>
                      <Button size="sm" variant="outline-secondary" title="Alinhar à esquerda" onClick={() => aplicarComando('justifyLeft')}>
                        <FontAwesomeIcon icon={faAlignLeft} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Centralizar" onClick={() => aplicarComando('justifyCenter')}>
                        <FontAwesomeIcon icon={faAlignCenter} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Alinhar à direita" onClick={() => aplicarComando('justifyRight')}>
                        <FontAwesomeIcon icon={faAlignRight} />
                      </Button>
                      <span className="mx-1 text-muted">|</span>
                      <Button size="sm" variant="outline-secondary" title="Inserir cabeçalho" onClick={inserirCabecalho}>
                        <FontAwesomeIcon icon={faHeading} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Inserir rodapé" onClick={inserirRodape}>
                        <FontAwesomeIcon icon={faGripLines} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Inserir tabela" onClick={inserirTabela}>
                        <FontAwesomeIcon icon={faTable} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Inserir logomarca" onClick={selecionarLogomarca}>
                        <FontAwesomeIcon icon={faImage} />
                      </Button>
                      <Button size="sm" variant="outline-secondary" title="Inserir quebra de página" onClick={inserirQuebraPagina}>
                        <FontAwesomeIcon icon={faFileAlt} />
                      </Button>
                      <span className="mx-1 text-muted">|</span>
                      <Button size="sm" variant="outline-secondary" title="Limpar formatação" onClick={() => aplicarComando('removeFormat')}>
                        <FontAwesomeIcon icon={faEraser} />
                      </Button>
                    </div>

                    <div className="bg-light p-3" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                      <div
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        className="mx-auto bg-white border"
                        onInput={atualizarConteudoEditor}
                        onBlur={atualizarConteudoEditor}
                        onPaste={colarConteudoFormatado}
                        style={{
                          boxShadow: '0 2px 10px rgba(0, 0, 0, .08)',
                          fontFamily: 'Arial, sans-serif',
                          fontSize: 14,
                          lineHeight: 1.5,
                          maxWidth: '100%',
                          minHeight: 820,
                          outline: 'none',
                          padding: '48px 56px',
                          wordBreak: 'break-word',
                          width: 794,
                        }}
                      />
                    </div>
                  </div>
                )}
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button type="button" variant="outline-secondary" onClick={preVisualizar}>
                <FontAwesomeIcon icon={faEye} className="me-2" />
                Pré-visualizar
              </Button>
              <Button type="button" id="App-button-cor-principal-escura" onClick={salvarTemplate}>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Salvar
              </Button>
            </div>

            {previewHtml && (
              <div className="mt-3 border rounded">
                <iframe
                  title="Pré-visualização do contrato"
                  srcDoc={previewHtml}
                  style={{ width: '100%', minHeight: 320, border: 0 }}
                />
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PromotoraContratoTemplateModal;
